// Icon registry - imports only the icons we actually use
// This enables tree-shaking and dramatically reduces bundle size from 37MB to ~100KB
import React from "react"
import {
  IconCrossLarge,
  IconDotGrid1x3Vertical,
  IconMagnifyingGlass,
  IconPaperclip2,
  IconPlusLarge,
  IconTrashPermanently,
  IconExclamationTriangle,
  IconCircleX,
  IconCircleCheck,
  IconCirclePlus,
  IconChevronGrabberVertical,
  IconSquareArrowTopRight,
  IconChevronDownSmall,
  IconChevronLeftSmall,
  IconChevronTopSmall,
  IconChevronRightSmall,
  IconChevronBottom,
  IconChevronLeft,
  IconChevronTop,
  IconChevronRight,
  IconPeopleCircle,
  IconPuzzle,
  IconArrowBoxLeft,
  IconEmail3,
  IconCalendar1,
  IconBubbleAnnotation6,
  IconGroup1,
  IconMapPin,
  IconClock,
  IconSquareChecklist,
  IconArrowRight,
  IconLoader,
  IconPayment,
  IconSquarePlaceholderDashed,
  IconSettingsGear1,
  IconRetouch,
  IconClockDefault,
  IconCheckmark2,
  IconTrashCanSimple,
  IconArrowsRepeatCircle,
  IconArrowRounded,
} from "@central-icons-react/round-filled-radius-2-stroke-2"

// Import outlined versions
import {
  IconCrossLarge as IconCrossLargeOutlined,
  IconDotGrid1x3Vertical as IconDotGrid1x3VerticalOutlined,
  IconMagnifyingGlass as IconMagnifyingGlassOutlined,
  IconPaperclip2 as IconPaperclip2Outlined,
  IconPlusLarge as IconPlusLargeOutlined,
  IconTrashPermanently as IconTrashPermanentlyOutlined,
  IconTrashCanSimple as IconTrashCanSimpleOutlined,
  IconArrowsRepeatCircle as IconArrowsRepeatCircleOutlined,
  IconArrowRounded as IconArrowRoundedOutlined,
  IconExclamationTriangle as IconExclamationTriangleOutlined,
  IconCircleX as IconCircleXOutlined,
  IconCircleCheck as IconCircleCheckOutlined,
  IconCirclePlus as IconCirclePlusOutlined,
  IconChevronGrabberVertical as IconChevronGrabberVerticalOutlined,
  IconSquareArrowTopRight as IconSquareArrowTopRightOutlined,
  IconChevronDownSmall as IconChevronDownSmallOutlined,
  IconChevronLeftSmall as IconChevronLeftSmallOutlined,
  IconChevronTopSmall as IconChevronTopSmallOutlined,
  IconChevronRightSmall as IconChevronRightSmallOutlined,
  IconChevronBottom as IconChevronBottomOutlined,
  IconChevronLeft as IconChevronLeftOutlined,
  IconChevronTop as IconChevronTopOutlined,
  IconChevronRight as IconChevronRightOutlined,
  IconPeopleCircle as IconPeopleCircleOutlined,
  IconPuzzle as IconPuzzleOutlined,
  IconArrowBoxLeft as IconArrowBoxLeftOutlined,
  IconEmail3 as IconEmail3Outlined,
  IconCalendar1 as IconCalendar1Outlined,
  IconBubbleAnnotation6 as IconBubbleAnnotation6Outlined,
  IconGroup1 as IconGroup1Outlined,
  IconMapPin as IconMapPinOutlined,
  IconClock as IconClockOutlined,
  IconSquareChecklist as IconSquareChecklistOutlined,
  IconArrowRight as IconArrowRightOutlined,
  IconLoader as IconLoaderOutlined,
  IconPayment as IconPaymentOutlined,
  IconSquarePlaceholderDashed as IconSquarePlaceholderDashedOutlined,
  IconSettingsGear1 as IconSettingsGear1Outlined,
  IconRetouch as IconRetouchOutlined,
  IconClockDefault as IconClockDefaultOutlined,
  IconCheckmark2 as IconCheckmark2Outlined,
} from "@central-icons-react/round-outlined-radius-2-stroke-2"

import { IconName, ActiveIconName } from "./Icon"

// Type for React icon component
// The actual components accept size as string | number, so we use a more flexible type
type IconComponent = React.ComponentType<{
  size?: number | string
  color?: string
  stroke?: string | number
  radius?: string | number
}>

// Registry mapping icon names to their filled components
const filledIconRegistry: Record<string, IconComponent> = {
  IconCrossLarge,
  IconDotGrid1x3Vertical,
  IconMagnifyingGlass,
  IconPaperclip2,
  IconPlusLarge,
  IconTrashPermanently,
  IconTrashCanSimple,
  IconArrowsRepeatCircle,
  IconArrowRounded,
  IconExclamationTriangle,
  IconCircleX,
  IconCircleCheck,
  IconCirclePlus,
  IconChevronGrabberVertical,
  IconSquareArrowTopRight,
  IconChevronDownSmall,
  IconChevronLeftSmall,
  IconChevronTopSmall,
  IconChevronRightSmall,
  IconChevronBottom,
  IconChevronLeft,
  IconChevronTop,
  IconChevronRight,
  IconPeopleCircle,
  IconPuzzle,
  IconArrowBoxLeft,
  IconEmail3,
  IconCalendar1,
  IconBubbleAnnotation6,
  IconGroup1,
  IconMapPin,
  IconClock,
  IconSquareChecklist,
  IconArrowRight,
  IconLoader,
  IconPayment,
  IconSquarePlaceholderDashed,
  IconSettingsGear1,
  IconRetouch,
  IconClockDefault,
  IconCheckmark2,
}

// Registry mapping icon names to their outlined components
const outlinedIconRegistry: Record<string, IconComponent> = {
  IconCrossLarge: IconCrossLargeOutlined,
  IconDotGrid1x3Vertical: IconDotGrid1x3VerticalOutlined,
  IconMagnifyingGlass: IconMagnifyingGlassOutlined,
  IconPaperclip2: IconPaperclip2Outlined,
  IconPlusLarge: IconPlusLargeOutlined,
  IconTrashPermanently: IconTrashPermanentlyOutlined,
  IconTrashCanSimple: IconTrashCanSimpleOutlined,
  IconArrowsRepeatCircle: IconArrowsRepeatCircleOutlined,
  IconArrowRounded: IconArrowRoundedOutlined,
  IconExclamationTriangle: IconExclamationTriangleOutlined,
  IconCircleX: IconCircleXOutlined,
  IconCircleCheck: IconCircleCheckOutlined,
  IconCirclePlus: IconCirclePlusOutlined,
  IconChevronGrabberVertical: IconChevronGrabberVerticalOutlined,
  IconSquareArrowTopRight: IconSquareArrowTopRightOutlined,
  IconChevronDownSmall: IconChevronDownSmallOutlined,
  IconChevronLeftSmall: IconChevronLeftSmallOutlined,
  IconChevronTopSmall: IconChevronTopSmallOutlined,
  IconChevronRightSmall: IconChevronRightSmallOutlined,
  IconChevronBottom: IconChevronBottomOutlined,
  IconChevronLeft: IconChevronLeftOutlined,
  IconChevronTop: IconChevronTopOutlined,
  IconChevronRight: IconChevronRightOutlined,
  IconPeopleCircle: IconPeopleCircleOutlined,
  IconPuzzle: IconPuzzleOutlined,
  IconArrowBoxLeft: IconArrowBoxLeftOutlined,
  IconEmail3: IconEmail3Outlined,
  IconCalendar1: IconCalendar1Outlined,
  IconBubbleAnnotation6: IconBubbleAnnotation6Outlined,
  IconGroup1: IconGroup1Outlined,
  IconMapPin: IconMapPinOutlined,
  IconClock: IconClockOutlined,
  IconSquareChecklist: IconSquareChecklistOutlined,
  IconArrowRight: IconArrowRightOutlined,
  IconLoader: IconLoaderOutlined,
  IconPayment: IconPaymentOutlined,
  IconSquarePlaceholderDashed: IconSquarePlaceholderDashedOutlined,
  IconSettingsGear1: IconSettingsGear1Outlined,
  IconRetouch: IconRetouchOutlined,
  IconClockDefault: IconClockDefaultOutlined,
  IconCheckmark2: IconCheckmark2Outlined,
}

/**
 * Get the React component for an icon by name
 * Returns null if icon not found (shouldn't happen with our enum)
 */
export function getIconComponent(
  name: IconName | ActiveIconName,
  isFilled: boolean,
): IconComponent | null {
  const registry = isFilled ? filledIconRegistry : outlinedIconRegistry
  return registry[name] || null
}
